import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, ShieldCheck, AlertTriangle, MapPin, Gauge, Calendar, Car,
  Phone, Mail, ExternalLink, TrendingDown, TrendingUp, Minus, Copy, Check,
  ChevronLeft, ChevronRight, X, Expand,
} from "lucide-react";
import { toast } from "sonner";

function BadgePill({ value }) {
  const map = {
    "Great Deal": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    "Fair Price": "bg-blue-500/10 text-blue-300 border-blue-500/20",
    "Authorized": "bg-white/5 text-gray-300 border-white/10",
    "Anomaly": "bg-yellow-500/10 text-yellow-300 border-yellow-500/20",
  };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${map[value] || map.Authorized}`}>
      {value}
    </span>
  );
}

// Simple SVG bar chart: asking price vs rolling median, with tolerance bands.
function PriceComparisonChart({ price, median }) {
  if (!median) return null;
  const deltaPct = ((price - median) / median) * 100;
  const isLow = deltaPct <= -15;
  const isHigh = deltaPct >= 25;
  const min = Math.min(price, median) * 0.75;
  const max = Math.max(price, median) * 1.25;
  const range = max - min || 1;
  const scale = (v) => ((v - min) / range) * 100;

  const priceX = scale(price);
  const medianX = scale(median);
  const lowBand = scale(median * 0.85);
  const highBand = scale(median * 1.25);

  return (
    <div>
      <div className="relative h-16">
        {/* Tolerance band (green -15% to +25%) */}
        <div
          className="absolute top-0 h-full rounded bg-emerald-500/10 border-y border-emerald-500/20"
          style={{ left: `${lowBand}%`, width: `${highBand - lowBand}%` }}
        />
        {/* Median line */}
        <div
          className="absolute top-0 h-full w-px bg-white/40"
          style={{ left: `${medianX}%` }}
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-gray-400 whitespace-nowrap font-mono">
            Median ${median.toLocaleString()}
          </div>
        </div>
        {/* Asking price marker */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-black ${
            isLow ? "bg-emerald-400" : isHigh ? "bg-red-400" : "bg-blue-400"
          }`}
          style={{ left: `${priceX}%` }}
        >
          <div className={`absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] whitespace-nowrap font-mono ${
            isLow ? "text-emerald-300" : isHigh ? "text-red-300" : "text-blue-300"
          }`}>
            ${price.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-500 mt-8">
        <span>-25%</span>
        <span className="text-emerald-400/70">Fair range</span>
        <span>+25%</span>
      </div>
    </div>
  );
}

const FLAG_LABELS = {
  low_price: { label: "Priced below market", tone: "emerald", desc: "This vehicle is priced ≥15% below the rolling median for the same year/trim. Could be a great deal — or a signal of undisclosed history." },
  high_price: { label: "Priced above market", tone: "red", desc: "This vehicle is priced ≥25% above the rolling median for the same year/trim. Compare features carefully or negotiate." },
  no_photos: { label: "No photos provided", tone: "yellow", desc: "Dealer hasn't uploaded photos yet. Request photos before committing." },
};

export default function InventoryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [copied, setCopied] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    api.get(`/authorized-inventory/${id}`)
      .then((r) => setV(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    setSimilar([]);
    api.get(`/authorized-inventory/${id}/similar?limit=4`)
      .then((r) => setSimilar(r.data || []))
      .catch(() => setSimilar([]));
  }, [id]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      const n = (v?.image_urls || []).length;
      if (!n) return;
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowRight") setActiveImg((i) => (i + 1) % n);
      else if (e.key === "ArrowLeft") setActiveImg((i) => (i - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, v]);

  const copyVin = async () => {
    if (!v?.vin) return;
    try {
      await navigator.clipboard.writeText(v.vin);
      setCopied(true);
      toast.success("VIN copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
        <div className="animate-pulse space-y-6">
          <div className="h-6 w-40 bg-white/5 rounded" />
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 aspect-[16/10] bg-white/5 rounded-2xl" />
            <div className="lg:col-span-4 h-96 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !v) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 text-center">
        <h1 className="font-display text-4xl mb-3">Vehicle no longer available</h1>
        <p className="text-gray-400 mb-8">This listing may have sold or been removed. Browse other authorized inventory.</p>
        <Link to="/inventory" className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm">Back to Inventory</Link>
      </div>
    );
  }

  const hasImages = v.image_urls && v.image_urls.length > 0;
  const imgs = v.image_urls || [];
  const count = imgs.length;
  const safeIdx = count ? ((activeImg % count) + count) % count : 0;
  const goNext = () => count && setActiveImg((i) => (i + 1) % count);
  const goPrev = () => count && setActiveImg((i) => (i - 1 + count) % count);
  const deltaPct = v.price_delta_pct != null ? v.price_delta_pct * 100 : null;
  const deltaIcon = deltaPct == null ? Minus : deltaPct < 0 ? TrendingDown : deltaPct > 0 ? TrendingUp : Minus;
  const DeltaIcon = deltaIcon;

  const decoded = v.vin_decoded || {};
  const decodedRows = [
    { k: "Make", v: decoded["Make"] },
    { k: "Model", v: decoded["Model"] },
    { k: "Year", v: decoded["Model Year"] },
    { k: "Trim", v: decoded["Trim"] },
    { k: "Body", v: decoded["Body Class"] },
    { k: "Vehicle Type", v: decoded["Vehicle Type"] },
    { k: "Fuel", v: decoded["Fuel Type - Primary"] },
    { k: "Drivetrain", v: decoded["Drive Type"] },
    { k: "Cylinders", v: decoded["Engine Number of Cylinders"] },
  ].filter((r) => r.v);

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8 md:py-12" data-testid="inventory-detail-page">
      <button
        onClick={() => nav(-1)}
        className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5 mb-6"
        data-testid="inv-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Left column: gallery + specs */}
        <div className="lg:col-span-8 space-y-6">
          {/* Hero + thumbs (swipeable, clickable gallery) */}
          <div>
            <div
              className="relative aspect-[16/10] bg-[#050507] rounded-2xl overflow-hidden border border-[#1A1A24] group select-none"
              data-testid="inv-hero"
              onTouchStart={(e) => { e.currentTarget._tx = e.changedTouches[0].clientX; }}
              onTouchEnd={(e) => {
                if (!hasImages || count < 2) return;
                const dx = e.changedTouches[0].clientX - (e.currentTarget._tx || 0);
                if (dx < -40) goNext(); else if (dx > 40) goPrev();
              }}
            >
              {hasImages ? (
                <img
                  src={imgs[safeIdx]}
                  alt={`${v.year} ${v.make} ${v.model} — photo ${safeIdx + 1}`}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setLightbox(true)}
                  data-testid="inv-hero-image"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 gap-3">
                  <AlertTriangle className="w-10 h-10" />
                  <div className="text-sm">No photos available yet</div>
                  <div className="text-xs text-gray-600 max-w-xs text-center">Dealer hasn&apos;t uploaded photos. Request them via the contact form.</div>
                </div>
              )}
              <div className="absolute top-4 left-4 flex gap-2">
                <BadgePill value={v.badge} />
                <span className="text-xs px-2.5 py-1 rounded-full bg-black/60 text-white border border-white/10 backdrop-blur inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Authorized
                </span>
                {v.is_mocked && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-yellow-500/90 text-black font-mono font-bold">MOCKED</span>
                )}
              </div>
              {hasImages && (
                <>
                  <button
                    onClick={() => setLightbox(true)}
                    className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 border border-white/10 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Expand"
                    data-testid="inv-expand-btn"
                  >
                    <Expand className="w-4 h-4" />
                  </button>
                  {count > 1 && (
                    <>
                      <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 border border-white/10 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="inv-hero-prev" title="Previous photo">
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 border border-white/10 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="inv-hero-next" title="Next photo">
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-4 right-4 text-[11px] px-2.5 py-1 rounded-full bg-black/60 text-white border border-white/10 backdrop-blur font-mono" data-testid="inv-photo-counter">
                        {safeIdx + 1} / {count}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            {hasImages && count > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="inv-thumbs">
                {imgs.map((u, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${safeIdx === i ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}
                    data-testid={`inv-thumb-${i}`}
                  >
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Header block */}
          <div>
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <h1 className="font-display text-3xl md:text-5xl font-light tracking-tighter">
                {v.year} {v.make} {v.model}
              </h1>
              <span className="text-lg text-gray-400">{v.trim}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-400">
              <span className="inline-flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> {v.mileage?.toLocaleString()} mi</span>
              <span className="inline-flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> {v.condition?.toUpperCase()}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {v.location || "—"}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Listed {v.created_at ? new Date(v.created_at).toLocaleDateString() : "recently"}</span>
            </div>
          </div>

          {/* Anomaly notes */}
          {v.anomaly_flags?.length > 0 && (
            <div className="space-y-2" data-testid="inv-flags">
              {v.anomaly_flags.map((f) => {
                const meta = FLAG_LABELS[f] || { label: f, tone: "yellow", desc: "" };
                const toneMap = {
                  emerald: "bg-emerald-500/5 border-emerald-500/30 text-emerald-200",
                  red: "bg-red-500/5 border-red-500/30 text-red-200",
                  yellow: "bg-yellow-500/5 border-yellow-500/30 text-yellow-200",
                };
                return (
                  <div key={f} className={`p-4 rounded-2xl border ${toneMap[meta.tone]}`}>
                    <div className="font-medium text-sm mb-1">{meta.label}</div>
                    <div className="text-xs opacity-80 leading-relaxed">{meta.desc}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Price-vs-market card */}
          <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="inv-price-chart">
            {(() => {
              const mkt = v.market && v.market.value ? v.market : null;
              const marketVal = mkt ? mkt.value : (v.price_median || null);
              const dPct = mkt ? mkt.delta_pct * 100 : deltaPct;
              const dAmt = mkt ? mkt.delta_amount : (marketVal != null ? Math.round(v.price - marketVal) : null);
              const rating = mkt ? mkt.rating : null;
              const RATING_CLS = {
                "Great Deal": "bg-emerald-500/15 text-emerald-300",
                "Fair Price": "bg-blue-500/10 text-blue-300",
                "Above Market": "bg-yellow-500/15 text-yellow-300",
                "Overpriced": "bg-red-500/15 text-red-300",
              };
              return (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="label-mono mb-1">Price vs. Market</div>
                      <div className="font-display text-xl">How this listing prices out</div>
                    </div>
                    {rating ? (
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${RATING_CLS[rating] || "bg-white/5 text-gray-300"}`} data-testid="inv-deal-rating">{rating}</span>
                    ) : dPct != null ? (
                      <div className={`inline-flex items-center gap-1.5 text-sm font-mono px-3 py-1.5 rounded-full ${dPct <= -8 ? "bg-emerald-500/15 text-emerald-300" : dPct >= 10 ? "bg-red-500/15 text-red-300" : "bg-blue-500/10 text-blue-300"}`}>
                        <DeltaIcon className="w-3.5 h-3.5" />{dPct > 0 ? "+" : ""}{dPct.toFixed(1)}%
                      </div>
                    ) : null}
                  </div>

                  {marketVal != null ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6" data-testid="inv-market-stats">
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Listing Price</div>
                          <div className="font-display text-2xl">${(v.price || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">555 Market Value</div>
                          <div className="font-display text-2xl">${marketVal.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{dAmt <= 0 ? "Below Market" : "Above Market"}</div>
                          <div className={`font-display text-2xl ${dAmt <= 0 ? "text-emerald-300" : "text-yellow-300"}`}>
                            {dAmt <= 0 ? "−" : "+"}${Math.abs(dAmt).toLocaleString()}
                            <span className="text-sm ml-1">({dPct > 0 ? "+" : ""}{dPct.toFixed(1)}%)</span>
                          </div>
                        </div>
                      </div>
                      <PriceComparisonChart price={v.price} median={marketVal} />
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 mt-6">
                        {mkt && mkt.comp_count != null && <span data-testid="inv-comp-count">{mkt.comp_count} comparable vehicles</span>}
                        <span>Source: {mkt ? (mkt.source === "marketcheck" ? "MarketCheck live market data" : "555 verified inventory") : "555 verified inventory"}</span>
                        {mkt && mkt.fetched_at && <span data-testid="inv-market-updated">Updated {new Date(mkt.fetched_at).toLocaleDateString()}</span>}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-white/10 rounded-xl" data-testid="inv-no-market">
                      Not enough market data for this vehicle yet. Our team is sourcing comparable listings.
                    </div>
                  )}
                  <p className="text-[11px] text-gray-500 mt-6 leading-relaxed">
                    Market value uses live MarketCheck comparable data when available, falling back to verified 555 inventory. Deal rating: ≤−8% Great Deal · to +3% Fair Price · to +10% Above Market · higher Overpriced.
                  </p>
                </>
              );
            })()}
          </div>

          {/* VIN spec breakdown */}
          <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="inv-vin-specs">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="label-mono mb-1">VIN Decoded · NHTSA</div>
                <div className="font-display text-xl">Vehicle specification</div>
              </div>
              <button
                onClick={copyVin}
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 inline-flex items-center gap-1.5 font-mono"
                data-testid="inv-copy-vin"
              >
                {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                {v.vin}
              </button>
            </div>
            {decodedRows.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {decodedRows.map((r) => (
                  <div key={r.k} className="border-t border-white/5 pt-3">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{r.k}</div>
                    <div className="text-sm">{r.v}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">NHTSA hasn&apos;t returned decoded specs for this VIN yet.</div>
            )}
            <p className="text-[11px] text-gray-500 mt-6">Source: <a href="https://vpic.nhtsa.dot.gov/decoder/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white underline inline-flex items-center gap-1">NHTSA Product Information Catalog Vehicle Listing (vPIC) <ExternalLink className="w-3 h-3" /></a></p>
          </div>
        </div>

        {/* Right column: price + dealer + CTA */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-4">
            {/* Price box */}
            <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Asking Price</div>
              <div className="font-display text-5xl mb-4">${(v.price || 0).toLocaleString()}</div>
              <div className="flex flex-wrap gap-2 mb-6">
                {v.badge && <BadgePill value={v.badge} />}
                <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-emerald-400" /> Authorized dealer
                </span>
              </div>

              <div className="space-y-2">
                <Link
                  to={`/find-my-car?vin=${v.vin}&make=${v.make}&model=${v.model}`}
                  className="w-full block text-center py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm"
                  data-testid="inv-request-broker-btn"
                >
                  Have 555 Broker This Deal
                </Link>
                <a
                  href={`mailto:concierge@555autobrokers.com?subject=${encodeURIComponent(`Inquiry: ${v.year} ${v.make} ${v.model} (VIN ${v.vin?.slice(-8)})`)}&body=${encodeURIComponent(`Hi 555 Team,\n\nI'd like more info on this vehicle:\n${v.year} ${v.make} ${v.model} ${v.trim}\nVIN: ${v.vin}\nDealer: ${v.dealer_name}\nAsking: $${v.price?.toLocaleString()}\n\nThanks!`)}`}
                  className="w-full block text-center py-3 rounded-full border border-white/15 text-sm hover:bg-white/5 inline-flex items-center justify-center gap-2"
                  data-testid="inv-email-btn"
                >
                  <Mail className="w-4 h-4" /> Email a Broker
                </a>
                <Link
                  to="/booking"
                  className="w-full block text-center py-3 rounded-full border border-white/15 text-sm hover:bg-white/5 inline-flex items-center justify-center gap-2"
                  data-testid="inv-book-btn"
                >
                  <Phone className="w-4 h-4" /> Book a Consultation
                </Link>
              </div>
            </div>

            {/* Dealer card */}
            <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="inv-dealer-card">
              <div className="label-mono mb-3">Selling Dealer</div>
              <div className="font-display text-lg mb-1">{v.dealer_name}</div>
              <div className="text-sm text-gray-400 mb-4 inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {v.location || "Location on request"}
              </div>
              <div className="text-xs text-gray-500 leading-relaxed">
                This vehicle sits on the authorized dealer&apos;s lot. Our brokers negotiate directly on your behalf — you don&apos;t deal with the sales floor.
              </div>
              {v.source_url && (
                <a
                  href={v.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 text-xs text-gray-400 hover:text-white inline-flex items-center gap-1"
                  data-testid="inv-source-link"
                >
                  View dealer listing <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {/* Feed source note */}
            <div className="text-[11px] text-gray-500 px-2">
              Source: <span className="font-mono text-gray-400">{v.is_mocked ? "MOCK_FEED_v1 (demo)" : v.source_feed}</span> · Updated {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Similar vehicles rail */}
      {similar.length > 0 && (
        <div className="mt-16 md:mt-20 border-t border-[#1A1A24] pt-10" data-testid="inv-similar-rail">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="label-mono mb-1">You might also like</div>
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tighter">Similar vehicles</h2>
            </div>
            <Link to="/inventory" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5">
              View all <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {similar.map((s) => (
              <SimilarCard key={s.id} s={s} onNav={() => nav(`/inventory/${s.id}`)} />
            ))}
          </div>
        </div>
      )}

      {/* Fullscreen photo lightbox */}
      {lightbox && hasImages && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          data-testid="inv-lightbox"
          onClick={() => setLightbox(false)}
          onTouchStart={(e) => { e.currentTarget._tx = e.changedTouches[0].clientX; }}
          onTouchEnd={(e) => {
            if (count < 2) return;
            const dx = e.changedTouches[0].clientX - (e.currentTarget._tx || 0);
            if (dx < -40) goNext(); else if (dx > 40) goPrev();
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white"
            data-testid="inv-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={imgs[safeIdx]}
            alt={`${v.year} ${v.make} ${v.model} — photo ${safeIdx + 1}`}
            className="max-w-[92vw] max-h-[86vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="inv-lightbox-image"
          />
          {count > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white" data-testid="inv-lightbox-prev">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white" data-testid="inv-lightbox-next">
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
                <div className="text-sm text-white/80 font-mono" data-testid="inv-lightbox-counter">{safeIdx + 1} / {count}</div>
                <div className="hidden md:flex gap-2 max-w-[80vw] overflow-x-auto pb-1">
                  {imgs.map((u, i) => (
                    <button key={i} onClick={(e) => { e.stopPropagation(); setActiveImg(i); }} className={`shrink-0 w-16 h-11 rounded overflow-hidden border-2 ${safeIdx === i ? "border-white" : "border-transparent opacity-50 hover:opacity-90"}`} data-testid={`inv-lightbox-thumb-${i}`}>
                      <img src={u} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SimilarCard({ s, onNav }) {
  const img = s.image_urls && s.image_urls.length > 0 ? s.image_urls[0] : null;
  return (
    <button
      onClick={onNav}
      className="group text-left bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden hover:border-white/25 transition-colors"
      data-testid={`inv-similar-card-${s.id}`}
    >
      <div className="relative aspect-[16/11] bg-[#050507] overflow-hidden">
        {img ? (
          <img src={img} alt={`${s.year} ${s.make} ${s.model}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600">
            <Car className="w-8 h-8" />
          </div>
        )}
        {s.badge && (
          <div className="absolute top-2.5 left-2.5">
            <BadgePill value={s.badge} />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="text-sm font-medium truncate">{s.year} {s.make} {s.model}</div>
        <div className="text-xs text-gray-500 truncate mb-2">{s.trim}</div>
        <div className="flex items-center justify-between">
          <span className="font-display text-lg">${(s.price || 0).toLocaleString()}</span>
          <span className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Gauge className="w-3 h-3" /> {s.mileage?.toLocaleString()} mi</span>
        </div>
      </div>
    </button>
  );
}
